import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Users, MessageSquare, MessageCircle, Globe, Languages, Disc, Trophy, Activity } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TILES = [
  { key: "members",       label: "Mitglieder",   icon: Users,         color: "#00AFFF" },
  { key: "online",        label: "Online jetzt", icon: Activity,      color: "#22C55E" },
  { key: "forum_topics",  label: "Forum-Themen", icon: MessageSquare, color: "#A78BFA" },
  { key: "chat_messages", label: "Chat-Messages",icon: MessageCircle, color: "#F59E0B" },
  { key: "countries",     label: "Länder",       icon: Globe,         color: "#00AFFF" },
  { key: "languages",     label: "Sprachen",     icon: Languages,     color: "#F472B6" },
  { key: "collections",   label: "Sammlerstücke",icon: Disc,          color: "#60A5FA" },
  { key: "fan_pins",      label: "Pins auf Karte", icon: Trophy,      color: "#FACC15" },
];

function useCountUp(target, duration = 1200) {
  const [val, setVal] = useState(0);
  const fromRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    const to = Number(target) || 0;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function StatTile({ icon: Icon, label, value, color }) {
  const display = useCountUp(value);
  return (
    <div data-testid={`live-stat-${label}`} className="glass glass-hover rounded-2xl p-3 sm:p-4 lg:p-5 relative overflow-hidden">
      <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full opacity-10" style={{ background: color }} />
      <div className="flex items-center justify-between mb-1.5 sm:mb-2">
        <Icon size={14} className="sm:w-4 sm:h-4" style={{ color }} />
        <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.3em] text-white/40">live</div>
      </div>
      <div className="font-display font-black text-xl sm:text-2xl lg:text-3xl tabular-nums" style={{ color }}>
        {display.toLocaleString()}
      </div>
      <div className="text-[10px] sm:text-[11px] text-white/65 mt-0.5 sm:mt-1 font-body">{label}</div>
    </div>
  );
}

export default function LiveStats() {
  const [stats, setStats] = useState(null);
  const { t } = useI18n();

  useEffect(() => {
    const load = () => axios.get(`${API}/stats/live`).then((r) => setStats(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <section data-testid="live-stats-section" className="container mx-auto max-w-[1500px] px-4 sm:px-8 pb-6">
      <div className="flex items-center justify-between mb-4 sm:mb-5 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#00AFFF] mb-1">{t("home.live_eyebrow", "Community-Puls")}</div>
          <h2 className="font-display font-black text-xl sm:text-2xl lg:text-3xl silver-text tracking-tighter">{t("home.live_title", "Live-Statistiken")}</h2>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs text-white/50">
          <span className="w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" /> {t("home.live_refresh", "Aktualisiert alle 30 Sek.")}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3">
        {TILES.map((tile) => {
          const { key, ...rest } = tile;
          return <StatTile key={key} {...rest} value={stats?.[key] || 0} />;
        })}
      </div>
    </section>
  );
}
